import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/app/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.new_password.length < 6) { setErr("New password must be at least 6 characters"); return; }
    if (form.new_password !== form.confirm) { setErr("Passwords don't match"); return; }
    if (form.new_password === form.current_password) { setErr("New password must differ from current"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setDone(true);
      toast.success("Password changed");
      setTimeout(() => navigate("/app"), 1200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Change failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-xl mx-auto">
      <PageHeader
        title="Change password"
        subtitle="Update your account credentials. You'll stay signed in on this device."
      />

      {done ? (
        <div className="border border-[hsl(var(--status-pass))/0.4] bg-[hsl(var(--status-pass))/0.05] rounded-sm p-5 flex items-start gap-3" data-testid="cp-done">
          <ShieldCheck className="h-5 w-5 text-[hsl(var(--status-pass))] mt-0.5" />
          <div className="text-sm">Password updated. Redirecting to dashboard…</div>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-sm p-6" data-testid="change-password-card">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-4 w-4 text-primary" />
            <div className="overline">Account security</div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label className="overline mb-2 block">Current password</Label>
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={form.current_password}
                onChange={upd("current_password")}
                className="rounded-sm font-mono"
                data-testid="cp-current-input"
              />
            </div>
            <div>
              <Label className="overline mb-2 block">New password</Label>
              <Input
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={form.new_password}
                onChange={upd("new_password")}
                className="rounded-sm font-mono"
                data-testid="cp-new-input"
              />
            </div>
            <div>
              <Label className="overline mb-2 block">Confirm new password</Label>
              <Input
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={form.confirm}
                onChange={upd("confirm")}
                className="rounded-sm font-mono"
                data-testid="cp-confirm-input"
              />
            </div>

            {err && (
              <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="cp-error">
                {err}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={busy} className="rounded-sm bg-primary hover:bg-primary/90 h-11 px-6" data-testid="cp-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
